import { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Project, Endpoints, TFile } from '@brg/api';
import { FlexBox } from '@brg/ui';
import { useResizedImageEnhanced } from 'shared/hooks/useResizedImageEnhanced';
import { LayoutConst } from 'core';
import { getProjectImages } from '../utils/projectUtils';
import { getThemeByColor } from '../utils/colorUtils';
import { calcPos } from '../utils/positionUtils';

const CoverSlideWrapper = styled.div<{ bg?: string }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ bg }) => bg || '#FEFEFE'};
  position: relative;
  overflow: hidden;
`;

const CoverImageWrapper = styled.div<{ isWide?: boolean; isPro?: boolean }>`
  width: ${({ isPro }) => (isPro ? '100%' : '50%')};
  max-width: ${({ isPro }) => (isPro ? '100%' : '580px')};
  height: ${({ isWide, isPro }) => (isPro ? '100%' : isWide ? 'auto' : '100%')};
  max-height: ${({ isWide, isPro }) => (isPro ? '100%' : isWide ? '70vh' : '100%')};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: ${({ isPro }) => (isPro ? 'none' : 'rgba(61, 59, 89, 0.06) 0px 4px 4px')};
`;

const CoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ImageShadow = styled.div<{ position?: string }>`
  background: ${({ position }) =>
    position === 'default-top'
      ? 'linear-gradient(0deg, rgba(38, 42, 45, 0) 0%, rgba(22, 24, 26, 0.6) 100%)'
      : 'linear-gradient(180deg, rgba(38, 42, 45, 0) 0%, rgba(22, 24, 26, 0.6) 100%)'};
  min-height: 398px;
  ${({ position }) => (position === 'default-top' ? 'top: 0px;' : 'bottom: 0px;')}
  padding: 0 144px 48px 64px;
  width: 100%;
  position: absolute;
`;

const ImageTextWrapper = styled.div<{ position?: string }>`
  padding: 0 144px 48px 64px;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${({ position }) =>
    position &&
    css`
      ${calcPos(position)};
    `};
`;

const ImageTitle = styled.div<{ color?: string }>`
  color: ${({ color }) => color || '#FEFEFE'};
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 29px;
  font-style: normal;
  font-weight: 600;
  line-height: 39.5px;
  letter-spacing: -0.02em;
`;

const ImageSubTitle = styled.div<{ color?: string }>`
  color: ${({ color }) => color || '#FEFEFE'};
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 25px;
  letter-spacing: -0.02em;
`;

const ProjectTitle = styled.div<{ theme?: string }>`
  font-size: 85px;
  line-height: 112%;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${({ theme }) => (theme === 'white' ? '#151514' : '#FEFEFE')};
  margin-bottom: 48px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 72px;
  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    font-size: 28px;
    line-height: 120%;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 24px;
  }
`;

const DefaultLayoutWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  margin: 0 auto;
`;

const DefaultImageWrapper = styled.div`
  height: 100%;
  position: relative;
  box-shadow: rgba(61, 59, 89, 0.06) 0px 4px 4px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${LayoutConst.screen.md}px) {
    width: 50%;
    flex-basis: 50%;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 52%;
    flex-basis: 52%;
  }
`;

const DefaultTextWrapper = styled.div<{ theme?: string }>`
  display: flex;
  flex: 0 0 48%;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 47px 24px 0 70px;
  color: ${({ theme }) => (theme === 'white' ? '#151514' : '#FEFEFE')};
  min-width: 0;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 32px 24px 0 64px;
  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    padding: 24px 16px 0 32px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 16px 12px 0 16px;
  }

  ${ProjectTitle} {
    margin-bottom: 0;
  }
`;

interface CoverSlideProps {
  project?: Project;
  backgroundColor?: string;
  lang?: string;
  blocks?: any[];
  isTeam?: boolean;
}

const getBlockFiles = (block?: any): TFile[] => {
  if (!block) return [];
  if (Array.isArray(block?.attachments) && block.attachments.length > 0) {
    const files = block.attachments.flatMap((attachment: any) => attachment?.files || []);
    if (files.length) return files;
  }
  if (Array.isArray(block?.files) && block.files.length > 0) {
    return block.files;
  }
  return [];
};

const getCoverImagesFromBlocks = (blocks?: any[]): TFile[] => {
  if (!Array.isArray(blocks) || !blocks.length) {
    return [];
  }

  const asCoverBlock = blocks.find((block) => block?.style === 'as_cover' && getBlockFiles(block).length > 0);
  if (asCoverBlock) {
    return getBlockFiles(asCoverBlock);
  }

  const firstImageBlock = blocks.find((block) => getBlockFiles(block).length > 0);
  return getBlockFiles(firstImageBlock);
};

export const CoverSlide: FC<CoverSlideProps> = ({ project, backgroundColor, lang = 'ru', blocks, isTeam }) => {
  if (!project) return null;
  const projectImages = getProjectImages(project, lang);
  const fallbackImages = getCoverImagesFromBlocks(blocks);
  const landingImages = isTeam ? fallbackImages : projectImages;
  const coverFallback = isTeam ? projectImages : fallbackImages;
  const baseImages = landingImages?.length ? landingImages : coverFallback;
  const images = baseImages?.filter((i: any) => i?.size !== 'original') || [];
  const [resizedImg, containerRef] = useResizedImageEnhanced(images);
  const originalImg = useMemo(() => {
    if (!images.length) return null;
    return images.find((i: any) => i?.size === 'original') || images[images.length - 1];
  }, [images]);
  const img = resizedImg || originalImg;
  const isWide = useMemo(() => {
    if (!img || !img.width || !img.height) return false;
    return (img.width / img.height) >= 1.75;
  }, [img]);
  const theme = getThemeByColor(backgroundColor || '#FEFEFE');
  const isTeamLanding = Boolean((project as any)?.rootTeamId);
  const viewType = (project as any)?.longread?.viewType || (project as any)?.projectType;
  const isPro = ['fixed-ratio-small', 'fixed-ratio-large', 't-shape'].includes(viewType);

  const additionalContent = isTeamLanding
    ? (project as any)?.rootTeam?.additionalContent
    : (project as any)?.additionalContent;
  const additionalInfo = isTeamLanding
    ? (project as any)?.rootTeam?.additionalInfo
    : (project as any)?.additionalInfo;

  const title = additionalContent?.title;
  const subtitle = additionalContent?.subtitle;
  const position = additionalInfo?.titlePosition || 'default';
  const shouldShowProTitle = !project?.isTitleHidden && title?.visible === true;


  if (isPro) {
    return (
      <CoverSlideWrapper bg={backgroundColor}>
        <FlexBox
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <CoverImageWrapper isWide={isWide} isPro={isPro} ref={containerRef}>
            <CoverImage
              src={`${Endpoints.files}/${img?.url}`}
              alt={project?.title}
             
            />
            {shouldShowProTitle && (isTeamLanding || isPro) && (
              <ImageShadow position={position} />
            )}
            {shouldShowProTitle && (!isWide || isTeamLanding || isPro) && (
              <ImageTextWrapper position={position}>
                <ImageTitle color={title?.color}>
                  {title?.text || project?.title}
                </ImageTitle>
                {subtitle?.text && (
                  <ImageSubTitle color={subtitle?.color}>
                    {subtitle?.text}
                  </ImageSubTitle>
                )}
              </ImageTextWrapper>
            )}
          </CoverImageWrapper>
          {!project?.isTitleHidden && isWide && !isTeamLanding && !isPro && (
            <FlexBox
              width="100%"
              style={{ padding: '24px 64px 0', maxWidth: '1200px', margin: '0 auto' }}
            >
              <ProjectTitle theme={theme}>{project?.title}</ProjectTitle>
            </FlexBox>
          )}
        </FlexBox>
      </CoverSlideWrapper>
    );
  }

  return (
    <CoverSlideWrapper bg={backgroundColor}>
      <DefaultLayoutWrapper>
        <DefaultImageWrapper ref={containerRef}>
          <CoverImage
            src={`${Endpoints.files}/${img?.url}`}
            alt={project?.title}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              width: 'auto', 
              height: 'auto', 
              objectFit: 'contain' 
            }}
          />
        </DefaultImageWrapper>
        <DefaultTextWrapper theme={theme}>
          {!project?.isTitleHidden && (
            <ProjectTitle theme={theme}>{project?.title}</ProjectTitle>
          )}
        </DefaultTextWrapper>
      </DefaultLayoutWrapper>
    </CoverSlideWrapper>
  );
};
import { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Project, Endpoints, TFile } from '@brg/api';
import { FlexBox } from '@brg/ui';
import { useResizedImageEnhanced } from 'shared/hooks/useResizedImageEnhanced';
import { LayoutConst } from 'core';
import { getProjectImages } from '../utils/projectUtils';
import { getThemeByColor } from '../utils/colorUtils';
import { calcPos } from '../utils/positionUtils';

const CoverSlideWrapper = styled.div<{ bg?: string }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ bg }) => bg || '#FEFEFE'};
  position: relative;
  overflow: hidden;
`;

const CoverImageWrapper = styled.div<{ isWide?: boolean; isPro?: boolean }>`
  width: ${({ isPro }) => (isPro ? '100%' : '50%')};
  max-width: ${({ isPro }) => (isPro ? '100%' : '580px')};
  height: ${({ isWide, isPro }) => (isPro ? '100%' : isWide ? 'auto' : '100%')};
  max-height: ${({ isWide, isPro }) => (isPro ? '100%' : isWide ? '70vh' : '100%')};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: ${({ isPro }) => (isPro ? 'none' : 'rgba(61, 59, 89, 0.06) 0px 4px 4px')};
`;

const CoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ImageShadow = styled.div<{ position?: string }>`
  background: ${({ position }) =>
    position === 'default-top'
      ? 'linear-gradient(0deg, rgba(38, 42, 45, 0) 0%, rgba(22, 24, 26, 0.6) 100%)'
      : 'linear-gradient(180deg, rgba(38, 42, 45, 0) 0%, rgba(22, 24, 26, 0.6) 100%)'};
  min-height: 398px;
  ${({ position }) => (position === 'default-top' ? 'top: 0px;' : 'bottom: 0px;')}
  padding: 0 144px 48px 64px;
  width: 100%;
  position: absolute;
`;

const ImageTextWrapper = styled.div<{ position?: string }>`
  padding: 0 144px 48px 64px;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${({ position }) =>
    position &&
    css`
      ${calcPos(position)};
    `};
`;

const ImageTitle = styled.div<{ color?: string }>`
  color: ${({ color }) => color || '#FEFEFE'};
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 29px;
  font-style: normal;
  font-weight: 600;
  line-height: 39.5px;
  letter-spacing: -0.02em;
`;

const ImageSubTitle = styled.div<{ color?: string }>`
  color: ${({ color }) => color || '#FEFEFE'};
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 25px;
  letter-spacing: -0.02em;
`;

const ProjectTitle = styled.div<{ theme?: string }>`
  font-size: 85px;
  line-height: 112%;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${({ theme }) => (theme === 'white' ? '#151514' : '#FEFEFE')};
  margin-bottom: 48px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 72px;
  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    font-size: 28px;
    line-height: 120%;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 24px;
  }
`;

const DefaultLayoutWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  margin: 0 auto;
`;

const DefaultImageWrapper = styled.div`
  height: 100%;
  position: relative;
  box-shadow: rgba(61, 59, 89, 0.06) 0px 4px 4px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${LayoutConst.screen.md}px) {
    width: 50%;
    flex-basis: 50%;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 52%;
    flex-basis: 52%;
  }
`;

const DefaultTextWrapper = styled.div<{ theme?: string }>`
  display: flex;
  flex: 0 0 48%;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 47px 24px 0 70px;
  color: ${({ theme }) => (theme === 'white' ? '#151514' : '#FEFEFE')};
  min-width: 0;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 32px 24px 0 64px;
  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    padding: 24px 16px 0 32px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 16px 12px 0 16px;
  }

  ${ProjectTitle} {
    margin-bottom: 0;
  }
`;

interface CoverSlideProps {
  project?: Project;
  backgroundColor?: string;
  lang?: string;
  blocks?: any[];
  isTeam?: boolean;
}

const getBlockFiles = (block?: any): TFile[] => {
  if (!block) return [];
  if (Array.isArray(block?.attachments) && block.attachments.length > 0) {
    const files = block.attachments.flatMap((attachment: any) => attachment?.files || []);
    if (files.length) return files;
  }
  if (Array.isArray(block?.files) && block.files.length > 0) {
    return block.files;
  }
  return [];
};

const getCoverImagesFromBlocks = (blocks?: any[]): TFile[] => {
  if (!Array.isArray(blocks) || !blocks.length) {
    return [];
  }

  const asCoverBlock = blocks.find((block) => block?.style === 'as_cover' && getBlockFiles(block).length > 0);
  if (asCoverBlock) {
    return getBlockFiles(asCoverBlock);
  }

  const firstImageBlock = blocks.find((block) => getBlockFiles(block).length > 0);
  return getBlockFiles(firstImageBlock);
};

export const CoverSlide: FC<CoverSlideProps> = ({ project, backgroundColor, lang = 'ru', blocks, isTeam }) => {
  if (!project) return null;
  const projectImages = getProjectImages(project, lang);
  const fallbackImages = getCoverImagesFromBlocks(blocks);
  const landingImages = isTeam ? fallbackImages : projectImages;
  const coverFallback = isTeam ? projectImages : fallbackImages;
  const baseImages = landingImages?.length ? landingImages : coverFallback;
  const images = baseImages?.filter((i: any) => i?.size !== 'original') || [];
  const [resizedImg, containerRef] = useResizedImageEnhanced(images);
  const originalImg = useMemo(() => {
    if (!images.length) return null;
    return images.find((i: any) => i?.size === 'original') || images[images.length - 1];
  }, [images]);
  const img = resizedImg || originalImg;
  const isWide = useMemo(() => {
    if (!img || !img.width || !img.height) return false;
    return (img.width / img.height) >= 1.75;
  }, [img]);
  const theme = getThemeByColor(backgroundColor || '#FEFEFE');
  const isTeamLanding = Boolean((project as any)?.rootTeamId);
  const viewType = (project as any)?.longread?.viewType || (project as any)?.projectType;
  const isPro = ['fixed-ratio-small', 'fixed-ratio-large', 't-shape'].includes(viewType);

  const additionalContent = isTeamLanding
    ? (project as any)?.rootTeam?.additionalContent
    : (project as any)?.additionalContent;
  const additionalInfo = isTeamLanding
    ? (project as any)?.rootTeam?.additionalInfo
    : (project as any)?.additionalInfo;

  const title = additionalContent?.title;
  const subtitle = additionalContent?.subtitle;
  const position = additionalInfo?.titlePosition || 'default';
  const shouldShowProTitle = !project?.isTitleHidden && title?.visible === true;


  if (isPro) {
    return (
      <CoverSlideWrapper bg={backgroundColor}>
        <FlexBox
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <CoverImageWrapper isWide={isWide} isPro={isPro} ref={containerRef}>
            <CoverImage
              src={`${Endpoints.files}/${img?.url}`}
              alt={project?.title}
             
            />
            {shouldShowProTitle && (isTeamLanding || isPro) && (
              <ImageShadow position={position} />
            )}
            {shouldShowProTitle && (!isWide || isTeamLanding || isPro) && (
              <ImageTextWrapper position={position}>
                <ImageTitle color={title?.color}>
                  {title?.text || project?.title}
                </ImageTitle>
                {subtitle?.text && (
                  <ImageSubTitle color={subtitle?.color}>
                    {subtitle?.text}
                  </ImageSubTitle>
                )}
              </ImageTextWrapper>
            )}
          </CoverImageWrapper>
          {!project?.isTitleHidden && isWide && !isTeamLanding && !isPro && (
            <FlexBox
              width="100%"
              style={{ padding: '24px 64px 0', maxWidth: '1200px', margin: '0 auto' }}
            >
              <ProjectTitle theme={theme}>{project?.title}</ProjectTitle>
            </FlexBox>
          )}
        </FlexBox>
      </CoverSlideWrapper>
    );
  }

  return (
    <CoverSlideWrapper bg={backgroundColor}>
      <DefaultLayoutWrapper>
        <DefaultImageWrapper ref={containerRef}>
          <CoverImage
            src={`${Endpoints.files}/${img?.url}`}
            alt={project?.title}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              width: 'auto', 
              height: 'auto', 
              objectFit: 'contain' 
            }}
          />
        </DefaultImageWrapper>
        <DefaultTextWrapper theme={theme}>
          {!project?.isTitleHidden && (
            <ProjectTitle theme={theme}>{project?.title}</ProjectTitle>
          )}
        </DefaultTextWrapper>
      </DefaultLayoutWrapper>
    </CoverSlideWrapper>
  );
};
